import React, { useState } from 'react';
import { useFormik } from 'formik';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwDialog from '../../../components/TwDialog/TwDialog';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import { Characters, useAllowCharacters } from '../../../hooks/useAllowCharacters';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import '../Customers/CreateClient/CreateClient.scss';
import { useCreateCustomerContactMutation } from '../../../redux/api/customer/customerApi';
import { CreateContactClient, IContactClient } from '../../../redux/api/customer/customer.model';
import TwCheckbox from '../../../components/TwCheckboxes/TwCheckboxes';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import useAuth from '../../../providers/Auth/Auth.provider';

export interface IClientContacts {
  open: boolean;
  isFirst: boolean;
  customerId: string;
  onClose?: () => void;
  onSuccess?: (newContact: IContactClient) => void;
}

interface CreateContactFormValues {
  email_address: string;
  first_name: string;
  last_name: string;
  phone: string;
  phone_ext?: string;
}

const initialValues: CreateContactFormValues = {
  email_address: '',
  first_name: '',
  last_name: '',
  phone: '',
  phone_ext: ''
};

const CreateClientContact: React.FC<IClientContacts> = ({
  open,
  customerId,
  isFirst,
  onClose,
  onSuccess
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const { enableCustomerPortal } = useFeatureFlags();
  const handleKeyDownCharacters = useAllowCharacters(Characters.phoneNumber);
  const { addNotification } = useNotifications();
  const [createContact] = useCreateCustomerContactMutation();

  const [emailChecked, setEmailChecked] = useState<boolean>(false);

  const { values, handleChange, resetForm } = useFormik({
    initialValues: initialValues,
    onSubmit: () => undefined,
    enableReinitialize: true
  });

  const submitContact = (): void => {
    const customerContactFirstName = values.first_name.trim();
    const customerContactLastName = values.last_name.trim();

    if (!customerContactFirstName || !customerContactLastName) {
      const missingBoth = !customerContactFirstName && !customerContactLastName;
      logRocketTrack('Customer Management', {
        action: 'Create customer contact',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: `Missing required field(s): ${
          missingBoth ? 'First name, Last name' : !values.first_name ? 'First name' : 'Last name'
        }`,
        variant: 'error'
      });
    } else {
      const body: CreateContactClient = {
        client_id: customerId,
        first_name: customerContactFirstName,
        last_name: customerContactLastName,
        email_address: values?.email_address.trim(),
        phone: values.phone,
        phone_ext: values.phone_ext,
        create_user_and_send_invite: enableCustomerPortal ? emailChecked : false
      };
      createContact(body)
        .unwrap()
        .then((newContact) => {
          addNotification({
            message: 'Customer contact created',
            variant: 'success'
          });

          setEmailChecked(false);
          resetForm({ values: initialValues });
          onSuccess && onSuccess(newContact);
          onClose && onClose();
        })
        .catch((err) => {
          addNotification({
            message: err.data.message || 'Error creating customer contact, please try again',
            variant: 'error'
          });

          if (err.status === 409) {
            setEmailChecked(false);
            resetForm({ values: initialValues });
            onClose && onClose();
          }
        });
    }
  };

  const addSendInviteStyle: React.CSSProperties = {
    marginRight: '115px'
  };

  const DialogActions = (): JSX.Element[] => {
    const defaultActions = [
      <TwButton
        key="1"
        fullwidth={false}
        type={TwButtonType.secondary}
        label={isFirst ? 'Skip' : 'Cancel'}
        action={(): void => {
          resetForm({ values: initialValues });
          onClose && onClose();
        }}
      />,
      <TwButton
        key="2"
        fullwidth={false}
        type={TwButtonType.primary}
        label="Create"
        action={submitContact}
      />
    ];

    const inviteCheckbox: JSX.Element = (
      <div key="0" style={addSendInviteStyle}>
        <TwTooltip title="Add email to send an invite" placement="top-start">
          <TwCheckbox
            name="sendInvite"
            disabled={!values.email_address}
            label="Send invite to customer portal"
            checked={emailChecked}
            onInputChange={(): void => {
              setEmailChecked((prev) => !prev);
            }}
          />
        </TwTooltip>
      </div>
    );

    if (enableCustomerPortal) {
      defaultActions.unshift(inviteCheckbox);
    }

    return defaultActions;
  };

  return (
    <TwDialog
      open={open}
      onClickClose={(): void => {
        resetForm({ values: initialValues });
        onClose && onClose();
      }}
      size="sm"
      title={isFirst ? 'Create first contact' : 'New contact'}
      actions={DialogActions()}
      draggable={true}
    >
      <div className="create-client__wrapper tid-create-client__wrapper">
        <div className="create-client__form-section">
          <div className="create-client__inputs-container">
            <div className="create-client__two-column">
              <div className="tid-create-client__company-email create-client__two-column-item">
                <TwField
                  id="first_name"
                  name="first_name"
                  value={values.first_name}
                  label="First name"
                  inputType={TwInputTypes.text}
                  disabled={false}
                  onInputChange={handleChange}
                  error={false}
                />
              </div>

              <div className="tid-create-client__company-phone create-client__two-column-item">
                <TwField
                  id="last_name"
                  name="last_name"
                  value={values.last_name}
                  label="Last name"
                  inputType={TwInputTypes.text}
                  disabled={false}
                  onInputChange={handleChange}
                  error={false}
                />
              </div>
            </div>
            <div className="create-client__two-column">
              <div
                className="tid-create-client__company-email
                        create-client__two-column-item"
              >
                <TwField
                  id="email_address"
                  name="email_address"
                  value={values.email_address}
                  label="Contact email (optional)"
                  inputType={TwInputTypes.text}
                  disabled={false}
                  onInputChange={handleChange}
                  error={false}
                />
              </div>

              <div
                className="tid-create-client__company-phone 
                       create-client__two-column-item create-client__phone-input-container"
              >
                <TwField
                  id="phone"
                  name="phone"
                  value={values.phone}
                  label="Phone number (optional)"
                  inputType={TwInputTypes.text}
                  disabled={false}
                  onInputChange={handleChange}
                  onKeyDown={handleKeyDownCharacters}
                  error={false}
                  className="create-client__phone create-client__phone__no-right-border"
                />
                <TwField
                  id="phone_ext"
                  name="phone_ext"
                  value={values.phone_ext}
                  label=""
                  placeholder="Ext."
                  inputType={TwInputTypes.text}
                  disabled={false}
                  onInputChange={handleChange}
                  onKeyDown={handleKeyDownCharacters}
                  error={false}
                  className="create-client__extension create-client__extension__left-border"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </TwDialog>
  );
};

export default CreateClientContact;
